import {
  EXPERIMENT_APPROVAL_INSTRUCTION_MODE,
  type ExperimentApprovalInstructionMode,
} from '@gonfalon/experiments/instructions';
import type { Experiment, Iteration, Treatment } from '@gonfalon/experiments/types';

/**
 * Approval-request instructions as we read them in the UI. The server stores a list of instruction
 * objects, but the in-memory shape depends on the source: Immutable `List` (from Redux state) or a
 * plain array (from REST). The helpers below narrow to a plain array before reading, so callers can
 * pass either shape directly.
 */
export type ApprovalInstructionsInput = readonly unknown[] | { toJS: () => unknown } | undefined | null;

export type ApprovalInstructionReason = {
  label: string;
  text: string;
};

export type ExperimentApprovalDisplayState = {
  mode: ExperimentApprovalInstructionMode;
  experiment: Experiment;
  approvalReason?: ApprovalInstructionReason;
};

type InstructionShape = {
  kind?: string;
  winningTreatmentId?: string;
  winningReason?: string;
  changeJustification?: string;
  value?: { treatments?: { value?: unknown } };
};

const INSTRUCTION_KIND = {
  START: 'startIteration',
  STOP: 'stopIteration',
  SAVE_AND_START: 'saveAndStartNewIteration',
} as const;

function hasToJS(value: unknown): value is { toJS: () => unknown } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'toJS' in value &&
    typeof (value as Record<'toJS', unknown>).toJS === 'function'
  );
}

function asInstruction(raw: unknown): InstructionShape | undefined {
  if (typeof raw !== 'object' || raw === null) {
    return undefined;
  }
  return raw as InstructionShape;
}

function readTreatmentsValue(raw: unknown): Treatment[] | undefined {
  const instruction = asInstruction(raw);
  const treatments = instruction?.value?.treatments?.value;
  if (!Array.isArray(treatments) || treatments.length === 0) {
    return undefined;
  }
  return treatments as Treatment[];
}

function normalizeInstructionList(instructions: ApprovalInstructionsInput): unknown[] | undefined {
  if (instructions !== null && instructions !== undefined && hasToJS(instructions)) {
    const plain = instructions.toJS();
    return Array.isArray(plain) ? plain : undefined;
  }
  if (Array.isArray(instructions)) {
    return instructions;
  }
  return undefined;
}

function findInstructionByKind(list: unknown[], kind: string): InstructionShape | undefined {
  for (const raw of list) {
    const instruction = asInstruction(raw);
    if (instruction?.kind === kind) {
      return instruction;
    }
  }
  return undefined;
}

export function getApprovalInstructionMode(instructions: ApprovalInstructionsInput): ExperimentApprovalInstructionMode {
  const list = normalizeInstructionList(instructions);
  if (!list) {
    return EXPERIMENT_APPROVAL_INSTRUCTION_MODE.START;
  }
  if (findInstructionByKind(list, INSTRUCTION_KIND.STOP)) {
    return EXPERIMENT_APPROVAL_INSTRUCTION_MODE.STOP;
  }
  if (findInstructionByKind(list, INSTRUCTION_KIND.SAVE_AND_START)) {
    return EXPERIMENT_APPROVAL_INSTRUCTION_MODE.SAVE_AND_START;
  }
  return EXPERIMENT_APPROVAL_INSTRUCTION_MODE.START;
}

export function getInstructionTreatments(instructions: ApprovalInstructionsInput): Treatment[] | undefined {
  const list = normalizeInstructionList(instructions);
  if (!list) {
    return undefined;
  }
  for (const raw of list) {
    const treatments = readTreatmentsValue(raw);
    if (treatments) {
      return treatments;
    }
  }
  return undefined;
}

export function getStopInstructionTreatments(
  instructions: ApprovalInstructionsInput,
  experiment: Experiment,
): Treatment[] | undefined {
  const list = normalizeInstructionList(instructions);
  if (!list) {
    return undefined;
  }
  const stopInstruction = findInstructionByKind(list, INSTRUCTION_KIND.STOP);
  if (!stopInstruction) {
    return undefined;
  }
  const activeIteration = experiment.draftIteration ?? experiment.currentIteration;
  const treatments = activeIteration?.treatments ?? [];
  if (treatments.length === 0) {
    return undefined;
  }
  return treatments.map((t) => ({
    ...t,
    allocationPercent: t._id === stopInstruction.winningTreatmentId ? '100' : '0',
  }));
}

export function getStopInstructionReason(instructions: ApprovalInstructionsInput): string | undefined {
  const list = normalizeInstructionList(instructions);
  if (!list) {
    return undefined;
  }
  const stopInstruction = findInstructionByKind(list, INSTRUCTION_KIND.STOP);
  return stopInstruction?.winningReason;
}

export function getStopInstructionWinningTreatmentId(instructions: ApprovalInstructionsInput): string | undefined {
  const list = normalizeInstructionList(instructions);
  if (!list) {
    return undefined;
  }
  const stopInstruction = findInstructionByKind(list, INSTRUCTION_KIND.STOP);
  return stopInstruction?.winningTreatmentId;
}

export function getSaveAndStartInstructionChangeJustification(
  instructions: ApprovalInstructionsInput,
): string | undefined {
  const list = normalizeInstructionList(instructions);
  if (!list) {
    return undefined;
  }
  const saveAndStartInstruction = findInstructionByKind(list, INSTRUCTION_KIND.SAVE_AND_START);
  return saveAndStartInstruction?.changeJustification;
}

export function getApprovalInstructionReason(
  instructions: ApprovalInstructionsInput,
): ApprovalInstructionReason | undefined {
  const stopReason = getStopInstructionReason(instructions);
  if (stopReason) {
    return { label: 'Reason for stopping', text: stopReason };
  }

  const changeJustification = getSaveAndStartInstructionChangeJustification(instructions);
  if (changeJustification) {
    return { label: 'Reason for changes', text: changeJustification };
  }

  return undefined;
}

export function getShippingTreatments(treatments: Treatment[] | undefined): Treatment[] {
  return (treatments ?? []).filter((t) => parseFloat(t.allocationPercent) > 0);
}

export function applyInstructionTreatments(
  experiment: Experiment,
  treatments: Treatment[] | undefined,
  winningReason?: string,
): Experiment {
  if (!treatments && winningReason === undefined) {
    return experiment;
  }
  if (experiment.draftIteration) {
    return {
      ...experiment,
      draftIteration: {
        ...experiment.draftIteration,
        ...(treatments && { treatments }),
        ...(winningReason !== undefined && { winningReason }),
      },
    };
  }
  if (experiment.currentIteration) {
    return {
      ...experiment,
      currentIteration: {
        ...experiment.currentIteration,
        ...(treatments && { treatments }),
        ...(winningReason !== undefined && { winningReason }),
      },
    };
  }
  return experiment;
}

export function getExperimentApprovalDisplayState({
  experiment,
  instructions,
  fallbackMode,
}: {
  experiment: Experiment;
  instructions?: ApprovalInstructionsInput;
  fallbackMode: ExperimentApprovalInstructionMode;
}) {
  const mode = instructions ? getApprovalInstructionMode(instructions) : fallbackMode;
  const isStop = mode === EXPERIMENT_APPROVAL_INSTRUCTION_MODE.STOP;
  const instructionTreatments = instructions
    ? isStop
      ? getStopInstructionTreatments(instructions, experiment)
      : getInstructionTreatments(instructions)
    : undefined;
  const instructionStopReason = instructions && isStop ? getStopInstructionReason(instructions) : undefined;
  const experimentForDisplay = instructions
    ? applyInstructionTreatments(experiment, instructionTreatments, instructionStopReason)
    : experiment;

  return {
    mode,
    experiment: experimentForDisplay,
    approvalReason:
      getApprovalInstructionReason(instructions) ??
      getStopApprovalReason(mode, experimentForDisplay.draftIteration ?? experimentForDisplay.currentIteration),
  };
}

function getStopApprovalReason(
  mode: ExperimentApprovalInstructionMode,
  activeIteration: Iteration | undefined,
): ApprovalInstructionReason | undefined {
  if (mode !== EXPERIMENT_APPROVAL_INSTRUCTION_MODE.STOP || !activeIteration?.winningReason) {
    return undefined;
  }

  return {
    label: 'Reason for stopping',
    text: activeIteration.winningReason,
  };
}
